import {
  Component,
  Host,
  h,
  State,
  Prop,
  Event,
  EventEmitter,
} from '@stencil/core';
import {
  BasketHelper,
  BasketService,
  BasketRepository,
  Language,
  MoneyPrice,
  SessionHelper,
  SessionRepository,
  SessionService,
} from '@cesare';
import { QwButton } from '../shared/qw-button/qw-button';
import { debounceTime, switchMap } from 'rxjs';
import { roundPriceToTwoDecimals, getCurrencyText } from '../../core/utils';
import { isInCheckoutPage } from '../../core/app/app';

@Component({
  tag: 'qw-basket',
  styleUrl: 'qw-basket.scss',
  shadow: false,
})
export class QwBasket {
  @Prop() qwBasketShowEmptyButton: boolean = false;
  @Prop() qwBasketShowBookNowButton: boolean = false;
  @Prop() qwBasketShowPriceIfEmpty: boolean = false;
  @Prop() qwBasketShowTaxes: boolean = false;
  @Prop() qwBasketShowOnSiteTaxes: boolean = false;
  @Prop() qwBasketShowExcludedTaxes: boolean = false;
  @Prop() qwBasketCustomTaxes: string;
  @State() totalPrice: MoneyPrice;
  @State() totalPriceConverted: MoneyPrice;
  @State() totalFormattedPrice: string;
  @State() totalFormattedPriceConverted: string;
  @State() showConvertedPrice: boolean = false;
  @State() onSiteTaxes: string;
  @State() excludedTaxes: string;
  @State() taxesMessage: string;
  @State() isLoading: boolean;
  @State() sessionOccupancy: any;
  @State() numberOfAccommodation: any;
  @Event() qwBasketBookNow: EventEmitter<void>;
  @Event() qwBasketClickPrice: EventEmitter<void>;
  @Event() qwBasketIsAccommodationSatisfy: EventEmitter<{
    isAccommodationSatisfy: boolean;
    status: number;
  }>;

  public componentWillLoad() {
    SessionService.getSession().subscribe();
    SessionRepository.sessionLoaded$
      .pipe(
        switchMap((session) => {
          this.sessionOccupancy = SessionHelper.getSessionOccupancy(session);
          return BasketService.getBasket(session);
        }),
      )
      .subscribe();

    BasketRepository.basketWithPrice$
      .pipe(debounceTime(500))
      .subscribe((basket) => {
        this.totalPrice = BasketHelper.getTotalConvertedPrice(basket);
        this.totalPriceConverted = BasketHelper.getTotalOriginalPrice(basket);
        this.onSiteTaxes = this.formatPriceWithSymbol(basket.taxes.onSite);
        this.qwBasketShowOnSiteTaxes = basket.taxes.onSite.value.amount !== 0;
        this.excludedTaxes = this.formatPriceWithSymbol(basket.taxes.excluded);
        this.qwBasketShowExcludedTaxes =
          basket.taxes.excluded.value.amount !== 0;
        this.taxesMessage = BasketHelper.getTaxesFormatted(basket);
        this.numberOfAccommodation =
          BasketHelper.getOccupancyOfAccommodation(basket);
        this.qwBasketIsAccommodationSatisfy.emit(this.isAccommodationSatisfy());

        this.showConvertedPrice =
          this.totalPrice.value.currency !==
          this.totalPriceConverted.value.currency;
        this.totalFormattedPrice = this.formatPriceWithSymbol(this.totalPrice);
        this.totalFormattedPriceConverted = this.formatPriceWithSymbol(
          this.totalPriceConverted,
        );
      });
    BasketRepository.basketIsLoading$.subscribe(
      (isLoading) => (this.isLoading = isLoading),
    );
  }

  private deleteBasket() {
    BasketService.deleteBasket().subscribe();
  }

  bookNow = () => {
    this.qwBasketBookNow.emit();
  };

  private clickPrice() {
    this.qwBasketClickPrice.emit();
  }

  private isTotalPriceZero() {
    return !this.totalPrice || this.totalPrice.value.amount === 0;
  }

  private isAccommodationSatisfy() {
    const totalAdults = this.numberOfAccommodation?.reduce((acc, room) => {
      const adulti = room.occupancy.personCount || room.occupancy.adultCount;
      return acc + adulti;
    }, 0);
    const totalChildren = this.numberOfAccommodation?.reduce((acc, room) => {
      return acc + room.occupancy.childCount || 0;
    }, 0);
    const totalInfants = this.numberOfAccommodation?.reduce((acc, room) => {
      return acc + room.occupancy.infantCount || 0;
    }, 0);

    if (!this.numberOfAccommodation?.length) {
      return { isAccommodationSatisfy: false, status: 0 };
    } else if (
      !this.isOccupancySatisfied(totalAdults, totalChildren, totalInfants)
    ) {
      return { isAccommodationSatisfy: false, status: 2 };
    } else if (
      this.isOccupancySatisfied(totalAdults, totalChildren, totalInfants)
    ) {
      return { isAccommodationSatisfy: true, status: 1 };
    } else {
      return;
    }
  }

  private isOccupancySatisfied(totalAdults, totalChildren, totalInfants) {
    return (
      totalAdults >= this.sessionOccupancy?.adults &&
      totalChildren >= this.sessionOccupancy?.children &&
      totalInfants >= this.sessionOccupancy?.infants
    );
  }

  private formatPriceWithSymbol(price: MoneyPrice) {
    const currency = price.value.currency;
    return `${getCurrencyText(currency) || currency} ${roundPriceToTwoDecimals(
      price.value.amount,
    )}`;
  }

  public render() {
    return (
      <Host class={this.isAccommodationSatisfy() ? 'qw-basket--active' : ''}>
        {(!this.isTotalPriceZero() || this.qwBasketShowPriceIfEmpty) && (
          <div class="qw-basket__price" onClick={() => this.clickPrice()}>
            <span>{Language.getTranslation('total')}</span>
            <div
              class={`qw-basket__price-total ${
                this.isLoading ? 'qw-basket__price__amount--disabled' : ''
              }`}
            >
              {this.totalFormattedPrice}
              {this.qwBasketShowTaxes && !this.qwBasketShowExcludedTaxes && (
                <div
                  class={`qw-basket__tax-total ${
                    this.isLoading ? 'qw-basket__price__amount--disabled' : ''
                  }`}
                >
                  {this.qwBasketCustomTaxes
                    ? this.qwBasketCustomTaxes
                    : this.taxesMessage}
                </div>
              )}
              {this.showConvertedPrice && (
                <div
                  class={`qw-basket__price-total--converted ${
                    this.isLoading ? 'qw-basket__price__amount--disabled' : ''
                  }`}
                >
                  {`(${this.totalFormattedPriceConverted})`}
                </div>
              )}
            </div>
          </div>
        )}
        {this.qwBasketShowEmptyButton && (
          <QwButton
            QwButtonLabel={Language.getTranslation('emptyBasket')}
            QwButtonOnClick={this.deleteBasket}
          />
        )}
        {!this.isTotalPriceZero() && this.qwBasketShowExcludedTaxes && (
          <div
            class={`qw-basket__on-site-tax-total ${
              this.isLoading ? 'qw-basket__price__amount--disabled' : ''
            }  ${isInCheckoutPage() ? 'qw-basket__on-site-tax-total-checkout' : ''}`}
          >
            <span>{Language.getTranslation('excludedTaxes')}</span>{' '}
            {this.excludedTaxes}
          </div>
        )}
        {!this.isTotalPriceZero() && this.qwBasketShowOnSiteTaxes && (
          <div
            class={`qw-basket__on-site-tax-total ${
              this.isLoading ? 'qw-basket__price__amount--disabled' : ''
            } ${isInCheckoutPage() ? 'qw-basket__on-site-tax-total-checkout' : ''}`}
          >
            <span>{Language.getTranslation('excludedTaxes')}</span>{' '}
            {this.onSiteTaxes}
          </div>
        )}
        {this.qwBasketShowBookNowButton && (
          <QwButton
            QwButtonClass="qw-button--checkout"
            QwButtonLabel={Language.getTranslation('checkout')}
            QwButtonDisabled={
              !this.totalPrice ||
              this.isTotalPriceZero() ||
              !this.isAccommodationSatisfy()
            }
            QwButtonOnClick={this.bookNow}
          />
        )}
      </Host>
    );
  }
}
